export enum AnalyticsEvent {
  SIGNUP_STARTED = 'Signup Started',
  SET_USER_ID = 'Set User ID',
  SIGNUP_STEP_COMPLETED = 'Signup Step Completed',
  SIGNUP_COMPLETED = 'Signup Completed',
  USER_IDENTIFIED = 'User Identified',
  USER_LOGGED_OUT = 'User Logged Out',
  STORE_CREATED_REGISTRATION = 'Store Created registration',
  PURCHASE_COMPLETED = 'Purchase Completed',
  PURCHASE_FAILED = 'Purchase Failed',
  PURCHASE_CLICKED = 'Purchase Clicked',
  QUANTITY_CHANGED = 'Quantity Changed',
  PROMO_CODE_ENTERED = 'Promo Code Entered',
  STICKER_SELECTED = 'Sticker Selected',
  SHIPPING_OPTION_CHANGED = 'Shipping Option Changed',
  PROMO_CODE_OPENED = 'Promo Code Opened',
  EMAIL_LIST_SIGNUP_SHOWN = 'Email List Signup Shown',
  EMAIL_LIST_SIGNUP_DISMISSED = 'Email List Signup Dismissed',
  EMAIL_LIST_SIGNUP_SUBMITTED = 'Email List Signup Submitted',
  CHECKOUT_SESSION_REDIRECTED = 'Checkout Session Redirected',
  CHECKOUT_SESSION_FAILED = 'Checkout Session Failed',
}

type PlausibleEvents = {
  [AnalyticsEvent.SET_USER_ID]: { userId: string };
  [AnalyticsEvent.SIGNUP_STARTED]: { step: number };
  [AnalyticsEvent.SIGNUP_STEP_COMPLETED]: {
    step: number;
    userId?: string;
    hasStore?: boolean;
  };
  [AnalyticsEvent.SIGNUP_COMPLETED]: {
    userId: string;
    hasStore: boolean;
  };
  [AnalyticsEvent.USER_IDENTIFIED]: {
    userId: string;
    email?: string;
  };
  [AnalyticsEvent.USER_LOGGED_OUT]: {
    userId: string;
  };
  [AnalyticsEvent.STORE_CREATED_REGISTRATION]: {
    step: number;
    userId: string;
    storeName: string;
    hasStore: boolean;
  };
  [AnalyticsEvent.PURCHASE_COMPLETED]: {
    userId?: string;
    sessionId: string;
    listingId: string;
  };
  [AnalyticsEvent.PURCHASE_FAILED]: {
    userId?: string;
    listingId: string;
  };
  [AnalyticsEvent.PURCHASE_CLICKED]: {
    userId?: string;
    listingId: string;
    quantity: number;
    priceInCents: number;
    totalPrice: number;
    selectedStickerId: string;
    shippingOption: 'address' | 'donate';
  };
  [AnalyticsEvent.QUANTITY_CHANGED]: {
    listingId: string;
    quantity: number;
    priceInCents: number;
    totalPrice: number;
  };
  [AnalyticsEvent.PROMO_CODE_ENTERED]: {
    listingId: string;
    code: string;
  };
  [AnalyticsEvent.STICKER_SELECTED]: {
    listingId: string;
    stickerId: string;
  };
  [AnalyticsEvent.SHIPPING_OPTION_CHANGED]: {
    listingId: string;
    option: string;
  };
  [AnalyticsEvent.PROMO_CODE_OPENED]: {
    listingId: string;
  };
  [AnalyticsEvent.EMAIL_LIST_SIGNUP_SHOWN]: Record<string, never>;
  [AnalyticsEvent.EMAIL_LIST_SIGNUP_DISMISSED]: Record<string, never>;
  [AnalyticsEvent.EMAIL_LIST_SIGNUP_SUBMITTED]: {
    email: string;
  };
};

type EventParams = Record<string, string | number | boolean>;

export const sendAnalyticsEvent = (eventName: string, params?: EventParams) => {
  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not initialized');
    return;
  }

  window.gtag('event', eventName, {
    ...params,
    timestamp: new Date().toISOString(),
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setAnalyticsUserData = (userData: any) => {
  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not initialized');
    return;
  }

  // Set user ID for cross-platform tracking
  if (userData.userId) {
    window.gtag('config', import.meta.env.VITE_GOOGLE_ANALYTICS_ID!, {
      user_id: userData?.userId,
    });
  }

  // Set user properties
  window.gtag('set', 'user_properties', {
    ...userData,
    last_updated: new Date().toISOString(),
  });
};

export const trackEvent = <T extends keyof PlausibleEvents>(
  eventName: T,
  props?: PlausibleEvents[T]
) => {
  sendAnalyticsEvent(eventName, props);
};

export const identifyUser = (user: { id: string; email?: string } | null) => {
  if (user) {
    setUserId(user.id);
    sendAnalyticsEvent(AnalyticsEvent.USER_IDENTIFIED, {
      userId: user.id,
      email: user.email ?? '',
    });
  }
};

export const setUserId = (userId: string) => {
  setAnalyticsUserData({ userId });
};

export const initializeAnalytics = (
  user: { id: string; email?: string } | null
) => {
  if (user) {
    identifyUser(user);
  }
};
